/**
 * Voluum Flow Optimizer — shared types
 * For Media Devoted | Created By Ciro Autiero
 *
 * Used by both backend (Nest) and frontend (React). Mirrors the persisted
 * shape of `voluum_flow_optimizer_config` (campaign-keyed configuration)
 * plus the request/response payloads used by the controller and the
 * runner / preview engine.
 */

import type { RuleCompareTo } from './flow-optimizer-compare-to';
import type {
  FlowTagRankingMetric,
  FlowTagRankingOp,
  FlowTagRankingScope,
} from './flow-tag-rules/types';
import type { TagTrafficProvider } from './tag-rules/override-context';
import type { TrackerId } from './trackers/tracker-registry';
export type { FlowOptimizerSchedulerStatus } from './flow-optimizer/scheduler-status.types';

/** Top-level optimizer mode toggle. */
export type FlowOptimizerMode = 'STANDARD' | 'CUSTOM';

/** Voluum flow hierarchy layers we operate on. */
import type { FlowOptimizerApplyReport } from './flow-optimizer-apply-report.types';

export type FlowLayer = 'paths' | 'landers' | 'offers';

/** Numeric comparison operators supported in the exclude rule builder. */
export type RuleOperator = '<' | '<=' | '=' | '!=' | '>' | '>=';

/** Metric identifier (a key into the available metrics catalog). */
export type MetricId = string;

/**
 * A single action a matched rule GROUP performs. `'exclude'` pauses the element
 * in the weight engine (weight kept, no budget). The queue rotation executes
 * three mutually exclusive verbs, listed here from least to most final:
 *
 * - `'set-to-queue'` — off the tracker and BACK IN the queue line, so the next
 *   rotation may promote it again (legacy stored `'remove'` normalizes to this).
 * - `'set-to-waiting'` — off the tracker and PARKED in Waiting: not queue-ready,
 *   no queue stamp, `timesQueued` untouched. It never returns by itself; a human
 *   moves it back. Same landing place a capacity eviction uses.
 * - `'eliminate'` — off the tracker and tombstoned in the bank; only a manual
 *   "bring back" revives it.
 *
 * `'add-element'` is a MODIFIER that backfills the freed slot with the next
 * queued candidate of the matched item's own layer. A group may combine several
 * LOGICAL actions; when more than one rotation verb is present the MOST FINAL
 * wins: `eliminate` > `set-to-waiting` > `set-to-queue`.
 *
 * Two PATHS-layer verbs act on a matched path's OFFERS (MD-1147, partner caps):
 * - `'pause'` — every offer of the path that is at its cap leaves the tracker
 *   path (the bank link stays, marked paused, its weight kept). When no active
 *   offer would be left the whole path is eliminated instead, cap-stamped, and
 *   `'add-element'` backfills its slot.
 * - `'reactivate'` — the reverse: a paused offer whose cap is no longer reached
 *   returns to its path; a cap-eliminated path returns to the queue and is
 *   promoted again when the pool has room.
 * `'switch-off'` is a placeholder.
 */
export type RuleAction =
  | 'exclude'
  | 'weight'
  | 'pause'
  | 'reactivate'
  | 'switch-off'
  | 'set-to-queue'
  | 'set-to-waiting'
  | 'eliminate'
  | 'add-element';

/** How a `'weight'` rule shapes the matched elements' share of their pool. */
export type RuleWeightMode = 'static' | 'dynamic';

/**
 * Payload for the `'weight'` rule action (stored on the group next to
 * `actions`). In BOTH modes `percent` is a SHARED TOTAL for all matched
 * elements together, split among them proportionally to score:
 * - `static`: the matches are pinned to `percent`% of their pool; everyone
 *   else competes over the remainder.
 * - `dynamic`: the matches share `+percent` points on top of their computed
 *   weights; the other competitors give up that share (auto-scaled down when
 *   the pool cannot absorb it — flagged, never blocking).
 */
export interface RuleWeightConfig {
  mode: RuleWeightMode;
  /** Integer 1–100 (see WEIGHT_RULE_PERCENT_MIN/MAX in flow-optimizer-weight-rules). */
  percent: number;
}

/**
 * Recursive AND/OR/NOT rule node. Composed by the frontend; evaluated
 * server-side only. A leaf condition comes in THREE kinds: the original numeric
 * metric threshold (`kind: 'condition'` — every stored `exclude_json` uses this
 * shape and keeps deserializing/evaluating identically), a SCORE-RANKING check
 * (`kind: 'ranking'`) and a FLOW-TAG membership check (`kind: 'tag'`). The two
 * new kinds mirror the Flow Tag Rules page and need a rank index / tag set at
 * evaluation time; without that context they simply never match.
 */
export type RuleNode =
  | {
      kind: 'group';
      /** Stable id for the group. Recorded as the removal reason's rule id. */
      id?: string;
      /**
       * Optional human name for the group ("Kill low ROI"). Purely a label — it
       * never affects evaluation — but a flow with several rule groups is
       * unreadable when every one of them is just "Rule 2". Absent or blank
       * renders the positional fallback.
       */
      name?: string;
      op: 'AND' | 'OR';
      children: RuleNode[];
      /** Actions performed when this group matches. Absent/empty == `['exclude']`. */
      actions?: RuleAction[];
      /** Payload for the `'weight'` action; meaningless without it. */
      weight?: RuleWeightConfig;
      /**
       * @deprecated No longer read. Rotation now judges over the group's own
       * `dateRange` window and never rotates a no-stats element, which replaces
       * the old time gate. Kept optional so historical `exclude_json` still
       * deserializes; dropped on next save.
       */
      minTestMinutes?: number;
      /**
       * Optional date window the group's conditions are evaluated over. When
       * omitted, the group falls back to the layer's default rules range (the
       * first applicable formula group, or `last-7-days`) — i.e. legacy
       * behaviour. `custom` uses `dateRangeCustomFrom` / `dateRangeCustomTo`.
       */
      dateRange?: string;
      dateRangeCustomFrom?: string;
      dateRangeCustomTo?: string;
      /**
       * Bank path row ids (`path_bank.id`) this group SKIPS. A path named here
       * is never even considered by the group — distinct from `exclude`, which
       * is what happens when a path DOES match. Absent/empty = skip nothing.
       *
       * Bank ids, not tracker path ids: a tracker id is rewritten every time the
       * path is recreated (promote, restore), which would silently un-ignore it.
       * A tracker-only path has no bank row and so can never be named here.
       */
      ignoredPathBankIds?: string[];
    }
  | { kind: 'not'; child: RuleNode }
  | {
      kind: 'condition';
      metric: MetricId;
      op: RuleOperator;
      value: number;
      /**
       * When set, the right-hand side is another metric (× percent/100) over
       * its own window — `value` is then ignored. `dateRange` absent =
       * inherit the group's window. Missing RHS data ⇒ the condition is
       * false. See flow-optimizer-compare-to.ts.
       */
      compareTo?: RuleCompareTo;
    }
  | {
      /**
       * SCORE-RANKING condition — rank this element by an optimizer value
       * against its sibling pool. Reuses the Flow Tag Rules ranking contract
       * and the same ranking engine (`flow-tag-rules/ranking`). Matches when the
       * element is `highest` / `lowest`, or falls in the `top_percent` /
       * `bottom_percent` (which require `percent`, an integer 1–100).
       */
      kind: 'ranking';
      metric: FlowTagRankingMetric;
      op: FlowTagRankingOp;
      /** Required for `top_percent` / `bottom_percent` (integer 1–100). */
      percent?: number;
      /** Landers/offers pool; paths always rank flow-wide. Default `'path'`. */
      scope?: FlowTagRankingScope;
    }
  | {
      /**
       * FLOW-TAG membership condition — matches when the element currently
       * earns the named Flow-Optimizer ranking tag. `negate` flips it to
       * "does NOT carry the tag".
       */
      kind: 'tag';
      tagName: string;
      negate?: boolean;
    };

/** Discriminant guards for the {@link RuleNode} union (see the sibling file). */
export { isRankingNode, isTagNode } from './flow-optimizer-rule-nodes';

/** Per-layer exclude rules tree; null means "no rules → keep everything". */
export interface ExcludeRulesTree {
  paths: RuleNode | null;
  landers: RuleNode | null;
  offers: RuleNode | null;
}

/** Authoring mode for the body of a single formula group. */
export type FormulaGroupMode = 'sliders' | 'blocks';

/**
 * Visual block-builder AST node. Mirrors the AST used by the backend evaluator,
 * but lives here so the frontend can render the same shape that the parser
 * produces from a freeform expression.
 */
export type FormulaBlockNode =
  | { kind: 'num'; value: number }
  | { kind: 'metric'; metric: MetricId }
  | { kind: 'subref'; subId: string }
  | { kind: 'unary'; op: '-'; operand: FormulaBlockNode }
  | {
      kind: 'binary';
      op: '+' | '-' | '*' | '/' | '%';
      left: FormulaBlockNode;
      right: FormulaBlockNode;
    }
  | {
      kind: 'call';
      fn: 'min' | 'max' | 'abs' | 'log' | 'sqrt' | 'lowest';
      args: FormulaBlockNode[];
    };

/**
 * Reserved identifier prefix for subformula references in serialized expressions.
 * The double underscore avoids clashing with real metric ids exposed by the
 * Voluum metadata catalog.
 */
export const SUBFORMULA_REF_PREFIX = '__sub_';

export function makeSubformulaRefName(subId: string): string {
  return `${SUBFORMULA_REF_PREFIX}${subId}`;
}

export function isSubformulaRefName(name: string): boolean {
  return name.startsWith(SUBFORMULA_REF_PREFIX);
}

export function readSubformulaRefId(name: string): string | null {
  return isSubformulaRefName(name) ? name.slice(SUBFORMULA_REF_PREFIX.length) : null;
}

/** A weighted sub-metric inside a sliders-mode formula group. */
export interface FormulaSubMetric {
  /** Stable id for slider rebalancing. */
  id: string;
  metric: MetricId;
  /** Integer in [0, 100]; siblings inside the same group sum to 100 (or all zero). */
  weight: number;
  /** Automation formulas (02/11/2026): rank by the lowest value — `lowest(metric)`. */
  lowest?: boolean;
}

/** Body persisted when `mode === 'sliders'`. */
export interface FormulaSlidersBody {
  metrics: FormulaSubMetric[];
}

/**
 * Named sub-block within a blocks-mode group. Authors split the formula into
 * titled, optionally documented pieces; the master formula composes them via
 * subrefs for readability.
 */
export interface FormulaSubformula {
  id: string;
  name: string;
  description?: string;
  ast: FormulaBlockNode | null;
  /** Canonical text form; the master formula references this via `__sub_<id>`. */
  expression: string;
  /**
   * Weight (0–100) of this sub-formula within its group. The group's
   * sub-formulas share 100%; at evaluation each sub-formula's value is scaled
   * by its normalized weight before the master composes it. Undefined =
   * unset → treated as an equal share (outcome-neutral vs. legacy configs).
   */
  weight?: number;
}

/** Body persisted when `mode === 'blocks'`. */
export interface FormulaBlocksBody {
  /** Building blocks. May be empty (master is then the only formula source). */
  subformulas: FormulaSubformula[];
  /** Master formula AST: composes subformulas (and raw metrics, if desired). */
  master: FormulaBlockNode | null;
  /** Canonical text form of `master`; this is what `FormulaGroup.expression` mirrors. */
  masterExpression: string;
}

/**
 * Layers a formula group is allowed to score. Undefined means "all layers"
 * (back-compat for configs saved before per-layer scoping shipped).
 */
export type FormulaLayerScope = ReadonlyArray<FlowLayer>;

export const ALL_LAYERS: ReadonlyArray<FlowLayer> = ['paths', 'landers', 'offers'];

/** A single calculation formula sub-group. */
export interface FormulaGroup {
  id: string;
  name: string;
  /** Date range preset (`custom` uses `dateRangeCustomFrom` / `dateRangeCustomTo`). */
  dateRange: string;
  /** ISO YYYY-MM-DD when `dateRange === 'custom'`. */
  dateRangeCustomFrom?: string;
  dateRangeCustomTo?: string;
  /** Authoring mode for this group's body. Defaults to 'blocks' for legacy rows. */
  mode: FormulaGroupMode;
  /** Present iff mode === 'sliders'. */
  sliders?: FormulaSlidersBody;
  /** Present iff mode === 'blocks'. */
  blocks?: FormulaBlocksBody;
  /** Canonical text form recomputed on every change; consumed by the backend evaluator. */
  expression: string;
  /** Layers this formula scores. Undefined => all layers (legacy default). */
  appliesTo?: FormulaLayerScope;
  /**
   * Bank path row ids (`path_bank.id`) this formula does NOT score. Those paths
   * are judged by the remaining groups, whose weights re-share to 100% for them;
   * a path ignored by EVERY applicable group keeps its current weight and is
   * left out of the maths entirely (the same freeze a no-formula layer gets).
   *
   * Bank ids, not tracker path ids — see the note on the rule group variant.
   */
  ignoredPathBankIds?: string[];
}

/**
 * Per-layer weights for one formula group: layer -> integer weight in [0, 100].
 * Only the layers the group `appliesTo` carry a value; each layer's weights sum
 * to 100 across the groups applicable to it.
 */
export type PerLayerGroupWeight = Partial<Record<FlowLayer, number>>;

/**
 * Group id -> its weights. The canonical (post-migration) shape is per-layer
 * (`PerLayerGroupWeight`). A legacy flat `number` is tolerated on read — it
 * means "the same weight on every layer the group applies to" and is upgraded
 * to the per-layer shape by `migrateGroupWeightsToPerLayer`. Read individual
 * values through `readGroupWeight(gw, groupId, layer)`, never index directly.
 */
export type GroupWeights = Record<string, PerLayerGroupWeight | number>;

/** Master formula configuration: groups + their per-layer weights (each layer sums to 100). */
export interface FormulaConfig {
  groups: FormulaGroup[];
  /** Group id -> per-layer weights; each layer's weights sum to 100 across applicable groups. */
  groupWeights: GroupWeights;
}

/**
 * A named capacity rule: cap how many elements of one or more layers may be
 * ACTIVE at once (e.g. "at most 5 active paths"). Distinct from the metric
 * condition/exclude rules — it's a flat per-layer limit, not a predicate.
 * `max: null` means "Ignore" (unlimited / no cap). Drives the bank-backed
 * queue rotation: when the active count is below `max`, a queued candidate is
 * promoted; a `remove` rule freeing a slot lets the next queued element in.
 */
export interface CapacityRule {
  id: string;
  name: string;
  /** Layers this rule limits (>= 1). The `max` applies to each independently. */
  layers: FlowLayer[];
  /** Max active per targeted layer; `null` = Ignore (unlimited). */
  max: number | null;
  /**
   * FILL UP: treat `max` as a target, not just a ceiling — every rotation run
   * promotes from the queue until the pool sits at its cap. Opportunistic
   * only: an empty queue never blocks a removal (unlike `min`, which is a
   * floor the end state must hold). Meaningless without a numeric `max`.
   */
  fillToMax?: boolean;
  /**
   * MIN active per targeted layer — the floor the rotation must never leave the
   * pool below; `null`/absent = no floor of its own (the engine's built-in
   * default applies: 0 for the default bucket, 1 per rule group, so a routing
   * rule cannot lose its last path).
   *
   * A floor is judged on the END state, so a removal that comes with a
   * replacement is fine: eliminating one of 4 live paths with `min: 4` is
   * allowed exactly when a queued element can take the freed slot.
   */
  min?: number | null;
  /** PATHS scope (only meaningful when `layers` includes "paths"):
   *  `"default"` caps the flow's default-paths bucket; `"rules"` caps EACH
   *  rule-based routing group individually. Absent = `"default"` (legacy
   *  rules predate the split and were authored for the main rotation). */
  pathScope?: "default" | "rules";
}

/** Full Flow Optimizer config (matches Prisma model JSON columns). */
export interface FlowOptimizerConfig {
  campaignId: string;
  flowId: string;
  mode: FlowOptimizerMode;
  enabled: boolean;
  killSwitch: boolean;
  intervalMin: number;
  minWeight: number;
  maxWeight: number;
  /** Mirror of Voluum `flow_FlowUpdateResource`. Backend uses this for STANDARD mode. */
  standardJson: Record<string, unknown>;
  excludeRules: ExcludeRulesTree;
  formula: FormulaConfig;
  /** Named "max active per layer" capacity rules. Absent/empty = no caps. */
  capacityRules?: CapacityRule[];
  /** When ON, the scheduler runs the bank-backed queue rotation for this campaign. */
  autoRotate?: boolean;
  lastRunAt?: string | null;
  lastRunOk?: boolean | null;
  lastError?: string | null;
  /** Library package this config was last applied from; null = hand-authored. */
  packageId?: string | null;
  packageAppliedAt?: string | null;
  /** Last persisted write — with packageAppliedAt it derives "edited since applied". */
  updatedAt?: string | null;
}

/** Per-item preview row returned by /preview and /apply. */
export interface FlowOptimizerItemPreview {
  layer: FlowLayer;
  itemId: string;
  itemName: string;
  /**
   * Sibling-pool id — "flow:default" for default paths, "flow:group:<id>"
   * for paths inside a conditional group / Binom rule, and the owning path
   * id for landers and offers (offers are ONE pool per path). Items sharing
   * a parentId compete for the same 1000‰ budget.
   */
  parentId?: string;
  /**
   * Owning path id. Lets the UI bucket landers + offers under their parent
   * path even when the same lander is reused across several paths.
   * Undefined for path-layer items (the path is its own owner).
   */
  pathId?: string;
  /**
   * `path_bank.id` for a PATH item — the id a per-path ignore list stores, and
   * the only way the drawer can offer a stable option. Undefined for
   * landers/offers, and for a tracker-only path the bank has no row for.
   */
  pathBankId?: string;
  excluded: boolean;
  /**
   * True when the item was excluded specifically by an EXCLUSION RULE (not by
   * being inactive / having no metrics / no formula targeting its layer). These
   * items are PAUSED on apply (disabled, weight value kept) and are removed from
   * the budget pool entirely — they reserve nothing — so the remaining
   * (non-excluded) competitors share the full 100% of the layer budget.
   */
  ruleExcluded?: boolean;
  /**
   * True when the tracker has NO numbers for this element in the window. It is
   * deliberately kept out of scoring (a 0 would punish a brand-new element),
   * which used to collapse it into a bare `excluded: true` — indistinguishable
   * from inactive or rule-excluded. Carried on the wire so a tag rule can opt
   * in to exactly this population (`appliesTo: "no-stats"`).
   *
   * Conditionally spread: absent, not `false`, when the element HAS stats — so
   * a flow with nothing fresh serializes byte-identically to before.
   */
  noMetrics?: boolean;
  reason?: string;
  /**
   * Rule ACTIONS whose group conditions match this item (union across matching
   * groups), in display order: `exclude` | `set-to-queue` | `eliminate` |
   * `add-element`. Lets the preview show a per-action icon inline — the same
   * actions authored in the Rules tab — so a rule's effect reads at a glance,
   * not just `exclude`. Absent/empty when no rule matches the item.
   */
  ruleActions?: RuleAction[];
  /**
   * Present when a `'weight'` rule pinned (static) or boosted (dynamic) this
   * item. Weighted rows stay competitors (`passThrough: false`) and ARE
   * written on apply — unlike frozen rows. `appliedPerMille` is the pinned
   * slice (static) or the bonus actually granted (dynamic) after any fit
   * adjustment; `adjusted` marks pins scaled up in an all-pinned pool or a
   * bonus trimmed because the pool could not absorb it.
   */
  weightRule?: {
    mode: RuleWeightMode;
    /** The authored shared-total percent of the matched rule. */
    percent: number;
    /** Per-run key of the matched group (`group.id`, else pre-order `w<n>`). */
    ruleKey: string;
    ruleName?: string;
    /** This item's slice of the rule total BEFORE any fit adjustment (‰). */
    requestedPerMille?: number;
    appliedPerMille?: number;
    adjusted?: boolean;
  };
  /**
   * Where this item's metrics came from: `real` (live tracker traffic),
   * `simulated` (an editable demo override merged into the cube because there
   * was no real traffic), or `none` (no metrics at all → frozen). Lets the UI
   * badge simulated rows and explain why an element is/ isn't being scored.
   */
  metricsSource?: "real" | "simulated" | "none";
  rawScore: number;
  scoreShare: number;
  /** Current weight on the tracker (0–100 integer). */
  currentWeight: number;
  /** Same on a 0–1000 per-mille scale (precise long-tail share). */
  currentPerMille: number;
  /** Optimizer's allocated weight (0–100 integer; legacy field). */
  optimalWeight: number;
  /** Same on a 0–1000 per-mille scale; the source of truth for the UI delta. */
  optimalPerMille: number;
  /** `optimalPerMille - currentPerMille`. Positive => optimizer increased the item. */
  deltaPerMille: number;
  active: boolean;
  /**
   * True when the item was kept at its current weight (excluded items are
   * passed through untouched instead of being zeroed). The optimizer only
   * redistributes the share of non-pass-through peers.
   */
  passThrough: boolean;
  /**
   * True when this item's sibling group had infeasible [min, max] bounds
   * (n*min > budget or n*max < budget) and the allocator relaxed them to
   * floor(budget/n) / ceil(budget/n) so scores could still rank the items.
   * Optional/additive — absent means the configured bounds held.
   */
  boundsRelaxed?: boolean;
  /** Per-group breakdown for transparency in the UI. */
  breakdown: Array<{
    groupId: string;
    groupName: string;
    rawScore: number;
    weightedScore: number;
    /** Master formula group weight (0–100) used for `weightedScore`. */
    groupWeight: number;
    dateRange: string;
    /** Canonical text form of the expression that was evaluated. */
    expression?: string;
    /**
     * Metric values the formula evaluated against, keyed by metric id
     * (e.g. {impressions: 1500, clicks: 30, profit: 25.2}). Empty when no
     * metrics were available for this item / date-range — useful for
     * debugging why the score is 0.
     */
    metrics?: Record<string, number>;
    /**
     * Per-metric contribution attribution. Populated only when the formula
     * group is in `sliders` mode (each metric has an explicit weight); blocks
     * mode formulas evaluate an arbitrary AST and cannot be attributed.
     * `contribution = value * (weight / 100)` — the value the metric added to
     * the group's pre-groupWeight raw score.
     */
    contributions?: Array<{
      metric: MetricId;
      /** Slider weight in [0, 100]. */
      weight: number;
      /** Raw metric value from the cube. */
      value: number;
      /** value * (weight / 100). */
      contribution: number;
    }>;
    /**
     * Per-sub-formula attribution for `blocks` mode: each sub-formula's
     * evaluated value, its normalized weight (% within the group, present only
     * when the group is weighted), and the value it contributed
     * (`weight != null ? value * weight/100 : value`). Lets the hover show the
     * real math behind the group score.
     */
    subformulaContributions?: Array<{
      subformulaId: string;
      name: string;
      expression: string;
      /** Evaluated value of the sub-formula (before weight scaling). */
      value: number;
      /** Normalized weight (%) within the group; omitted when unweighted. */
      weight?: number;
      /** value * (weight / 100) when weighted, else the raw value. */
      contribution: number;
    }>;
  }>;
  /**
   * Raw performance metrics keyed by cube date-range (the same keys used by
   * the formula evaluator — usually a preset id like `last-7-days` or
   * `custom:<groupId>`). Lets the UI render a "Performance" panel showing
   * the actual stat fields (impressions, clicks, conversions, cost, profit,
   * roi, …) that drove the optimizer's decision — invaluable for spotting
   * when every item shows 0 because the cube returned no rows.
   */
  metricsByDateRange?: Record<string, Record<string, number>>;
  /**
   * DISPLAY-only per-lander variants for OFFER items, keyed by landerId. An
   * offer is weighted per-path (one pool), but its stats differ per lander it
   * sits under; this carries the per-(path,lander) Performance + score so the UI
   * can show an offer's metrics within the selected lander (path→landing→offer,
   * like the panel). The offer's optimalWeight/allocation stays per-path.
   */
  perLander?: Record<
    string,
    {
      rawScore: number;
      breakdown: FlowOptimizerItemPreview["breakdown"];
      metricsByDateRange?: FlowOptimizerItemPreview["metricsByDateRange"];
    }
  >;
}

/**
 * A path allocation pool: the default-paths set or one conditional/rule
 * group. Path items reference their pool via `parentId`.
 */
export interface FlowOptimizerPool {
  /** Pool key as carried by path items' `parentId` (e.g. `flow:default`, `flow:group:<id>`). */
  id: string;
  /** Display name — "Default paths" or the Voluum group / Binom rule name. */
  name: string;
}

/** Response shape for /preview and /apply. */
export interface FlowOptimizerPreviewResult {
  campaignId: string;
  flowId: string;
  flowName: string;
  mode: FlowOptimizerMode;
  /** Items grouped by layer; per-layer the active items' optimalWeight always sums to 100. */
  items: FlowOptimizerItemPreview[];
  /** Path pools in display order (default first). Optional/additive. */
  pools?: FlowOptimizerPool[];
  /**
   * SMSWiser country this tracker campaign maps to; '' when the campaign is
   * unlinked or shared by several countries. Lets the client resolve
   * per-country flow-tag thresholds exactly like the server did.
   * Optional/additive.
   */
  countryKey?: string;
  /**
   * Tracker this campaign belongs to. Paired with `campaignId` it lets the
   * client resolve per-CAMPAIGN flow-tag thresholds exactly like the server
   * did — a campaign id alone is ambiguous across providers.
   * Optional/additive.
   */
  provider?: TagTrafficProvider;
  totals: {
    totalItems: number;
    excludedCount: number;
    includedCount: number;
    /**
     * Number of sibling groups whose [min, max] bounds were infeasible and
     * relaxed by the allocator (see FlowOptimizerItemPreview.boundsRelaxed).
     * Optional/additive — absent or 0 means every group's bounds held.
     */
    boundsRelaxedGroups?: number;
    /**
     * Number of sibling groups where a weight rule's target had to be
     * adjusted to fit (see FlowOptimizerItemPreview.weightRule.adjusted).
     * Optional/additive — absent or 0 means every weight rule fit as authored.
     */
    weightRuleAdjustedGroups?: number;
  };
  /** True only on /apply when the new flow body was successfully PUT to Voluum. */
  applied: boolean;
  appliedAt?: string;
  error?: string;
  /**
   * Per-element outcome of an /apply. Optional/additive — absent on /preview.
   * `applied: true` with `applyReport.skipped > 0` is a PARTIAL success: the
   * weights below DID land; the named elements did not.
   */
  applyReport?: FlowOptimizerApplyReport;
}

/** Metric definition exposed by GET /flow-optimizer/metadata. */
export interface FlowOptimizerMetricMeta {
  id: MetricId;
  name: string;
  category: 'traffic' | 'conversions' | 'financial' | 'rates' | 'advanced' | 'cap';
  /** A picker heading when a catalog mixes sources (the Automation's drops: tracker vs SMSWiser); absent = one "Metrics" list. */
  section?: string;
}

/** Metadata bundle for dropdowns in the FE. */
export interface FlowOptimizerMetadata {
  metrics: FlowOptimizerMetricMeta[];
  dateRanges: Array<{ id: string; name: string }>;
  redirectModes: Array<'REGULAR' | 'HTML' | 'DOUBLE_HTML' | 'REDIRECTLESS'>;
  conditionTypes: string[];
}

/** One row of the Optimizer Control Center list (GET /{provider}/flow-optimizer/configs). */
export interface FlowOptimizerConfigListItem {
  provider: TrackerId;
  campaignId: string;
  /** Display name; falls back to the raw id when the provider lookup fails. */
  campaignName: string;
  flowId: string;
  flowName: string;
  mode: FlowOptimizerMode;
  enabled: boolean;
  killSwitch: boolean;
  intervalMin: number;
  minWeight: number;
  maxWeight: number;
  lastRunAt?: string | null;
  lastRunOk?: boolean | null;
  lastError?: string | null;
  formulaSummary: {
    groupCount: number;
    /** Per-group summary: group name, its display expression (sub-formula
     *  refs resolved to names, truncated), per-layer weight, and each named
     *  sub-formula with its own expression + weight. Powers the formula-cell
     *  hover. */
    groups: Array<{
      name: string;
      expression: string;
      /** Per-layer weight (0–100) for the layers this group scores; each
       *  layer's weights sum to 100 across the groups applicable to it. */
      weights: Partial<Record<FlowLayer, number>>;
      subformulas: Array<{
        name: string;
        expression: string;
        /** Normalized share (%) within the group; set only when weighted. */
        weight?: number;
      }>;
    }>;
  };
  /** Campaign country (name or ISO code) for the flag column; null if unknown. */
  country?: string | null;
  /** The single Static Tag assigned to this campaign, if any. */
  staticTag?: { id: string; name: string; color: string } | null;
  /** Library package this config was last applied from, if any. `edited` =
   *  the config was saved again after the apply, so its content may have
   *  drifted from the package. */
  package?: {
    id: string;
    name: string;
    color: string | null;
    edited?: boolean;
  } | null;
  updatedAt?: string | null;
}
